import React, { useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FaSearch, FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { Modal, Button, Form } from 'react-bootstrap';


function SubCategoryList() {

    // Subcategory list
    const [subcategories, setSubcategories] = useState([]);

    // Category list
    const [categories, setCategories] = useState([]);

    // Search and pagination
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(100);

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Edit state
    const [isEditing, setIsEditing] = useState(false);

    // IDs
    const [subcategoryId, setSubcategoryId] = useState(null);
    const [categoryId, setCategoryId] = useState('');

    // Subcategory name
    const [subcategoryName, setSubcategoryName] = useState('');


    // =========================================================
    // GET ALL CATEGORIES
    // =========================================================

    const fetchCategories = async () => {

        const token = localStorage.getItem('authToken');

        if (!token) {
            return;
        }

        try {

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/getcategories`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch categories');
            }

            const result = await response.json();

            setCategories(result.data || []);

        } catch (error) {

            console.error(
                'Error fetching categories:',
                error
            );

        }
    };


    // =========================================================
    // GET ALL SUBCATEGORIES
    // =========================================================

    const fetchSubcategories = async () => {

        const token = localStorage.getItem('authToken');

        if (!token) {
            return;
        }

        try {

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/getsubcategory`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(
                    'Failed to fetch subcategories'
                );
            }

            const result = await response.json();

            setSubcategories(result.data || []);

        } catch (error) {

            console.error(
                'Error fetching subcategories:',
                error
            );

        }
    };


    // =========================================================
    // INITIAL LOAD
    // =========================================================

    useEffect(() => {

        fetchCategories();
        fetchSubcategories();

    }, []);


    // =========================================================
    // SEARCH
    // =========================================================

    const filteredSubcategories =
        subcategories.filter((subcategory) => {

            const subcategoryMatch =
                subcategory.subcategory &&
                subcategory.subcategory
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase());

            const categoryMatch =
                subcategory.category &&
                subcategory.category
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase());

            return subcategoryMatch || categoryMatch;

        });


    // =========================================================
    // PAGINATION
    // =========================================================

    const indexOfLastItem =
        currentPage * itemsPerPage;

    const indexOfFirstItem =
        indexOfLastItem - itemsPerPage;

    const currentSubcategories =
        filteredSubcategories.slice(
            indexOfFirstItem,
            indexOfLastItem
        );

    const totalPages =
        Math.ceil(
            filteredSubcategories.length /
            itemsPerPage
        );


    const handlePageChange = (pageNumber) => {

        setCurrentPage(pageNumber);

    };


    // =========================================================
    // ADD / EDIT SUBCATEGORY
    // =========================================================

    const handleAddOrEditSubcategory = async () => {

        const token =
            localStorage.getItem('authToken');

        if (!categoryId) {

            alert('Please select a category');

            return;
        }

        if (!subcategoryName.trim()) {

            alert('Please enter subcategory name');

            return;
        }

        try {

            const method =
                isEditing ? 'PUT' : 'POST';

            const url =
                isEditing
                    ? `${process.env.REACT_APP_API_URL}/updatesubcategory/${subcategoryId}`
                    : `${process.env.REACT_APP_API_URL}/addsubcategory`;


            const response = await fetch(
                url,
                {
                    method,

                    headers: {
                        'Authorization':
                            `Bearer ${token}`,

                        'Content-Type':
                            'application/json',
                    },

body: JSON.stringify({
    category_id: Number(categoryId),
    subcategory_name: subcategoryName.trim(),
}),
                }
            );


            if (!response.ok) {

                throw new Error(
                    isEditing
                        ? 'Failed to update subcategory'
                        : 'Failed to add subcategory'
                );

            }


            await response.json();


            // Refresh list
            await fetchSubcategories();


            // Close modal
            setShowModal(false);


            // Reset form
            resetForm();


        } catch (error) {

            console.error(
                'Error adding/updating subcategory:',
                error
            );

            alert(error.message);

        }
    };


    // =========================================================
    // EDIT SUBCATEGORY
    // =========================================================

    const handleEditClick = (subcategory) => {

        setSubcategoryId(
            subcategory.subcategory_id
        );

        setCategoryId(
            subcategory.category_id
        );

        setSubcategoryName(
            subcategory.subcategory
        );

        setIsEditing(true);

        setShowModal(true);

    };


    // =========================================================
    // DELETE CLICK
    // =========================================================

    const handleDeleteClick = (subcategory_id) => {

        setSubcategoryId(
            subcategory_id
        );

        setShowDeleteModal(true);

    };


    // =========================================================
    // DELETE SUBCATEGORY
    // =========================================================

    const handleDeleteSubcategory = async () => {

        const token =
            localStorage.getItem('authToken');

        try {

            const response = await fetch(

                `${process.env.REACT_APP_API_URL}/deletesubcategory/${subcategoryId}`,

                {
                    method: 'DELETE',

                    headers: {
                        'Authorization':
                            `Bearer ${token}`,
                    },
                }

            );


            if (!response.ok) {

                throw new Error(
                    'Failed to delete subcategory'
                );

            }


            await fetchSubcategories();


            setShowDeleteModal(false);


            setSubcategoryId(null);


        } catch (error) {

            console.error(
                'Error deleting subcategory:',
                error
            );

            alert(error.message);

        }

    };


    // =========================================================
    // RESET FORM
    // =========================================================

    const resetForm = () => {

        setSubcategoryName('');

        setCategoryId('');

        setSubcategoryId(null);

        setIsEditing(false);

    };


    // =========================================================
    // MODAL CLOSE
    // =========================================================

    const handleModalClose = () => {

        resetForm();

        setShowModal(false);

    };


    // =========================================================
    // OPEN ADD MODAL
    // =========================================================

    const handleAddClick = () => {

        resetForm();

        setShowModal(true);

    };


    // =========================================================
    // RETURN UI
    // =========================================================

    return (

        <div
            className="container"
            style={{ marginTop: '80px' }}
        >

            {/* PAGE TITLE */}

            <h2 className="text-primary">
                SubCategory List
            </h2>


            {/* SEARCH + ADD */}

            <div
                className="d-flex justify-content-between align-items-center mb-4"
            >

                <div
                    className="input-group me-3"
                    style={{
                        maxWidth: '300px'
                    }}
                >

                    <span
                        className="input-group-text bg-primary text-white"
                    >
                        <FaSearch />
                    </span>


                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search subcategories..."
                        value={searchTerm}
                        onChange={(e) => {

                            setSearchTerm(
                                e.target.value
                            );

                            setCurrentPage(1);

                        }}
                    />

                </div>


                <button
                    className="btn btn-success"
                    onClick={handleAddClick}
                >

                    <FaPlus /> Add SubCategory

                </button>

            </div>


            {/* TABLE */}

            <table
                className="table table-bordered table-striped"
            >

                <thead className="table-primary">

                    <tr>

                        <th>ID</th>

                        <th>Category</th>

                        <th>SubCategory</th>

                        <th>Created At</th>

                        <th>Actions</th>

                    </tr>

                </thead>


                <tbody>

                    {currentSubcategories.length > 0 ? (

                        currentSubcategories.map(
                            (subcategory) => (

                                <tr
                                    key={subcategory.subcategory_id}>
                                    <td>{subcategory.subcategory_id}</td>
                                    <td>{subcategory.category_id}</td>
                                    <td>{subcategory.subcategory}</td>
                                    <td>{subcategory.createdAt ? new Date(subcategory.createdAt).toLocaleDateString() : ''}</td>
                                    <td> <button className="btn btn-sm btn-outline-success me-2" onClick={() => handleEditClick(subcategory)}>
                                            <FaEdit /> Edit
                                        </button>

                                        <button
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() =>
                                                handleDeleteClick(
                                                    subcategory.subcategory_id
                                                )
                                            }
                                        >
                                            <FaTrash /> Delete
                                        </button>
                                    </td>

                                </tr>

                            )
                        )

                    ) : (

                        <tr>

                            <td
                                colSpan="5"
                                className="text-center"
                            >

                                No subcategories found

                            </td>

                        </tr>

                    )}

                </tbody>

            </table>


            {/* PAGINATION */}

            <nav>

                <ul
                    className="pagination justify-content-center"
                >

                    {[...Array(totalPages)].map(
                        (_, index) => (

                            <li
                                key={index}
                                className={
                                    `page-item ${
                                        currentPage ===
                                        index + 1
                                            ? 'active'
                                            : ''
                                    }`
                                }
                            >

                                <button
                                    className="page-link"
                                    onClick={() =>
                                        handlePageChange(
                                            index + 1
                                        )
                                    }
                                >

                                    {index + 1}

                                </button>

                            </li>

                        )
                    )}

                </ul>

            </nav>


            {/* ADD / EDIT MODAL */}

            <Modal
                show={showModal}
                onHide={handleModalClose}
            >

                <Modal.Header closeButton>

                    <Modal.Title>

                        {
                            isEditing
                                ? 'Edit SubCategory'
                                : 'Add New SubCategory'
                        }

                    </Modal.Title>

                </Modal.Header>


                <Modal.Body>

                    <Form>

                        {/* CATEGORY */}

                        <Form.Group
                            controlId="subcategoryCategory"
                        >

                            <Form.Label>
                                Category
                            </Form.Label>


                            <Form.Select
                                value={categoryId}
                                onChange={(e) =>
                                    setCategoryId(
                                        e.target.value
                                    )
                                }
                            >

                                <option value="">
                                    Select Category
                                </option>


                                {categories.map(
                                    (category) => (

                                        <option
                                            key={
                                                category.category_id
                                            }
                                            value={
                                                category.category_id
                                            }
                                        >

                                            {
                                                category.category
                                            }

                                        </option>

                                    )
                                )}

                            </Form.Select>

                        </Form.Group>


                        {/* SUBCATEGORY */}

                        <Form.Group
                            controlId="subcategoryName"
                            className="mt-3"
                        >

                            <Form.Label>
                                SubCategory Name
                            </Form.Label>


                            <Form.Control
                                type="text"
                                placeholder="Enter subcategory name"
                                value={subcategoryName}
                                onChange={(e) =>
                                    setSubcategoryName(
                                        e.target.value
                                    )
                                }
                            />

                        </Form.Group>

                    </Form>

                </Modal.Body>


                <Modal.Footer>

                    <Button
                        variant="secondary"
                        onClick={handleModalClose}
                    >
                        Close
                    </Button>


                    <Button
                        variant="primary"
                        onClick={
                            handleAddOrEditSubcategory
                        }
                    >

                        {
                            isEditing
                                ? 'Update SubCategory'
                                : 'Add SubCategory'
                        }

                    </Button>

                </Modal.Footer>

            </Modal>


            {/* DELETE MODAL */}

            <Modal
                show={showDeleteModal}
                onHide={() =>
                    setShowDeleteModal(false)
                }
            >

                <Modal.Header closeButton>

                    <Modal.Title>
                        Confirm Deletion
                    </Modal.Title>

                </Modal.Header>


                <Modal.Body>

                    Are you sure you want to delete
                    this subcategory?

                </Modal.Body>


                <Modal.Footer>

                    <Button
                        variant="secondary"
                        onClick={() =>
                            setShowDeleteModal(false)
                        }
                    >
                        Cancel
                    </Button>


                    <Button
                        variant="danger"
                        onClick={
                            handleDeleteSubcategory
                        }
                    >
                        Delete
                    </Button>

                </Modal.Footer>

            </Modal>

        </div>

    );

}


export default SubCategoryList;